/**
 * Renders source links for a thread using a Relay fragment on `ConvoAiHomeThreadSource`.
 * Maps the fragment data to SourcesType and delegates rendering to the shared SourcesPopupTrigger.
 */
import React from 'react';

import { graphql, useFragment } from 'react-relay';

import type { Sources as SourcesType } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { SourcesPopupTrigger } from '../../sources/SourcesPopupTrigger';

import type { Sources_AtlassianRovoForYou$key } from './__generated__/Sources_AtlassianRovoForYou.graphql';

interface SourcesProps {
	sources: Sources_AtlassianRovoForYou$key;
	testId?: string;
}

/** A click-triggered logo stack that shows a popup listing all source links for a thread. */
export const Sources = ({
	sources: sourcesRef,
	testId,
}: SourcesProps): React.JSX.Element | null => {
	const data = useFragment(
		graphql`
			fragment Sources_AtlassianRovoForYou on ConvoAiHomeThreadSource @relay(plural: true) {
				__typename
				... on ConvoAiHomeThreadsFirstPartySource {
					ari
					url
					data {
						__typename
					}
				}
				... on ConvoAiHomeThreadsThirdPartySource {
					url
				}
			}
		`,
		sourcesRef,
	);

	const mappedSources: SourcesType = data
		.filter(
			(source) =>
				source.__typename === 'ConvoAiHomeThreadsFirstPartySource' ||
				source.__typename === 'ConvoAiHomeThreadsThirdPartySource',
		)
		.map((source) => ({
			ari: source.__typename === 'ConvoAiHomeThreadsFirstPartySource' ? (source.ari ?? '') : '',
			title: '',
			type:
				source.__typename === 'ConvoAiHomeThreadsFirstPartySource' && source.data?.__typename
					? source.data.__typename
					: (source.__typename ?? ''),
			url: source.url ?? '',
			lastModified: '',
		}));

	return <SourcesPopupTrigger sources={mappedSources} testId={testId} />;
};
